import React, { useState, useEffect } from 'react';
import { Card, Tabs, InputNumber, Button, Table, message, Spin, Row, Col, Statistic, Alert, Form } from 'antd';
import { SaveOutlined, AlertOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import UniversalModuleModal from './UniversalModuleModal';
import { MODULE_MODAL_CONFIGS } from './moduleModalConfigs';

const { TabPane } = Tabs;

function Cpk({ fileId, fileName, setSelectedFile, initialConfig }) {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [inputData, setInputData] = useState({
    upper_specification: initialConfig?.upper_specification ?? 1.0,
    lower_specification: initialConfig?.lower_specification ?? 0.0,
    mean: initialConfig?.mean ?? null,
    standard_deviation: initialConfig?.standard_deviation ?? 1.0
  });
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (fileId) {
      retrieveAnalysis();
    }
  }, [fileId]);

  const retrieveAnalysis = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`/api/retrieve_cpk/${fileId}/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setInputData(response.data.input_data || {});
        setResults(response.data.output_data || null);
        if (response.data.output_data) {
          setActiveTab("2");
        }
      }
    } catch (error) {
      console.error("Error retrieving analysis:", error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        message.error("Failed to retrieve analysis.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("username");
    window.location.href = "/login";
  };

  const handleNumberChange = (name, value) => {
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSolve = async () => {
    if (inputData.upper_specification <= inputData.lower_specification) {
      message.error("Upper Specification Limit (USL) must be greater than Lower Specification Limit (LSL).");
      return;
    }
    if (inputData.standard_deviation < 0) {
      message.error("Standard Deviation cannot be negative.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const payload = {
        name: fileName || "Untitled Cpk Analysis",
        input_data: {
          upper_specification: Number(inputData.upper_specification),
          lower_specification: Number(inputData.lower_specification),
          mean: inputData.mean === null || inputData.mean === "" ? null : Number(inputData.mean),
          standard_deviation: Number(inputData.standard_deviation)
        }
      };

      let response;
      if (fileId) {
        response = await axios.put(`/api/update_cpk/${fileId}/`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`/api/save_cpk/`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response.data) {
        message.success("Analysis saved successfully!");
        setResults(response.data.output_data || null);

        if (!fileId && response.data.id) {
          setSelectedFile(prev => ({
            ...prev,
            id: response.data.id,
            name: response.data.name
          }));
        }

        setActiveTab("2");
      }
    } catch (error) {
      console.error("Error saving analysis:", error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        message.error(error.response?.data?.error || "Failed to save analysis.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getCpkInterpretation = (cpkValue) => {
    if (cpkValue === "∞") return {
      status: "success",
      title: "Perfect Capability (Standard Deviation is 0)",
      description: "Process has zero variation and is perfectly capable.",
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
    };

    const val = parseFloat(cpkValue);
    if (isNaN(val)) return null;

    if (val >= 1.33) {
      return {
        status: "success",
        title: `Capable Process (Cpk = ${val})`,
        description: "Excellent process capability. The process variation is well within specification limits.",
        color: "#52c41a",
        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
      };
    } else if (val >= 1.0) {
      return {
        status: "warning",
        title: `Marginally Capable Process (Cpk = ${val})`,
        description: "Adequate capability, but close to specification limits. Monitor closely for shift or wear.",
        color: "#faad14",
        icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />
      };
    } else {
      return {
        status: "error",
        title: `Incapable Process (Cpk = ${val})`,
        description: "Variation exceeds limits or process is off-center. High probability of producing defectives. Action required.",
        color: "#ff4d4f",
        icon: <AlertOutlined style={{ color: '#ff4d4f' }} />
      };
    }
  };

  const renderResults = () => {
    if (!results || !results.summary_data) return null;
    const data = results.summary_data;
    const interpretation = getCpkInterpretation(data.Process_capability_index_Cpk);

    const columns = [
      {
        title: 'Metric',
        dataIndex: 'metric',
        key: 'metric',
        render: (text) => <strong>{text}</strong>
      },
      {
        title: 'Value',
        dataIndex: 'value',
        key: 'value',
        align: 'right'
      }
    ];

    const dataSource = [
      { key: '1', metric: 'Upper Specification Limit (USL)', value: data.Upper_specification.toFixed(4) },
      { key: '2', metric: 'Lower Specification Limit (LSL)', value: data.Lower_specification.toFixed(4) },
      { key: '3', metric: 'Process Mean (μ)', value: data.Mean.toFixed(4) },
      { key: '4', metric: 'Standard Deviation (σ)', value: data.Standard_deviation.toFixed(4) },
      { key: '5', metric: 'Process Capability Index (Cpk)', value: typeof data.Process_capability_index_Cpk === 'number' ? data.Process_capability_index_Cpk.toFixed(4) : data.Process_capability_index_Cpk }
    ];

    return (
      <div style={{ marginTop: 16 }}>
        {interpretation && (
          <Alert
            message={interpretation.title}
            description={interpretation.description}
            type={interpretation.status}
            showIcon
            icon={interpretation.icon}
            style={{ marginBottom: 24, fontSize: '15px' }}
          />
        )}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Card style={{ textAlign: 'center', background: '#f6ffed', border: '1px solid #b7eb8f' }}>
              <Statistic
                title="Specification Range"
                value={data.Upper_specification - data.Lower_specification}
                precision={4}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card style={{ textAlign: 'center', background: '#e6f7ff', border: '1px solid #91d5ff' }}>
              <Statistic
                title="Process Mean"
                value={data.Mean}
                precision={4}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card
              style={{
                textAlign: 'center',
                background: interpretation?.status === 'success' ? '#f6ffed' : interpretation?.status === 'warning' ? '#fffbe6' : '#fff1f0',
                border: `1px solid ${interpretation?.color || '#d9d9d9'}`
              }}
            >
              <Statistic
                title="Process Capability (Cpk)"
                value={data.Process_capability_index_Cpk}
                precision={typeof data.Process_capability_index_Cpk === 'number' ? 4 : undefined}
                valueStyle={{ color: interpretation?.color, fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>

        <h3 style={{ marginBottom: 16 }}>Capability Parameters Summary</h3>
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          bordered
          size="middle"
        />
      </div>
    );
  };

  return (
    <Card
      title={`Process Capability (Cpk): ${fileName || 'Untitled Analysis'}`}
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSolve}
          loading={loading}
        >
          Solve & Save
        </Button>
      }
    >
      <Spin spinning={loading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Configuration" key="1">
            <Form layout="vertical" style={{ maxWidth: 500, margin: '24px auto' }}>
              <Form.Item label={<strong>Upper Specification Limit (USL)</strong>}>
                <InputNumber
                  style={{ width: '100%' }}
                  value={inputData.upper_specification}
                  onChange={(val) => handleNumberChange('upper_specification', val)}
                  step={0.1}
                />
              </Form.Item>
              <Form.Item label={<strong>Lower Specification Limit (LSL)</strong>}>
                <InputNumber
                  style={{ width: '100%' }}
                  value={inputData.lower_specification}
                  onChange={(val) => handleNumberChange('lower_specification', val)}
                  step={0.1}
                />
              </Form.Item>
              <Form.Item label={<strong>Process Mean (Optional)</strong>} help="Defaults to midpoint of USL and LSL if empty">
                <InputNumber
                  style={{ width: '100%' }}
                  value={inputData.mean}
                  onChange={(val) => handleNumberChange('mean', val)}
                  placeholder="Auto (Midpoint)"
                  step={0.1}
                />
              </Form.Item>
              <Form.Item label={<strong>Standard Deviation (σ)</strong>}>
                <InputNumber
                  style={{ width: '100%' }}
                  value={inputData.standard_deviation}
                  onChange={(val) => handleNumberChange('standard_deviation', val)}
                  min={0}
                  step={0.1}
                />
              </Form.Item>
            </Form>
          </TabPane>
          <TabPane tab="Analysis Results" key="2" disabled={!results}>
            {renderResults()}
          </TabPane>
        </Tabs>
      </Spin>
    </Card>
  );
}

export default Cpk;
